import { IDEMPOTENCY_HEADER } from '@rydres/shared';
import { ApiError, type ApiErrorBody, type Me, type Session } from './types.js';

export interface TokenStore {
  load(): Promise<Session | null> | Session | null;
  save(session: Session | null): Promise<void> | void;
}

/** Keeps the session in localStorage. */
export class LocalStorageTokenStore implements TokenStore {
  constructor(private readonly key = 'rydres.session') {}
  load(): Session | null {
    try {
      const raw = globalThis.localStorage?.getItem(this.key);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }
  save(session: Session | null): void {
    try {
      if (session) globalThis.localStorage?.setItem(this.key, JSON.stringify(session));
      else globalThis.localStorage?.removeItem(this.key);
    } catch {
      /* private mode or quota: the session lives in memory only */
    }
  }
}

export class MemoryTokenStore implements TokenStore {
  private s: Session | null = null;
  load() {
    return this.s;
  }
  save(session: Session | null) {
    this.s = session;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  store?: TokenStore;
  fetchFn?: typeof fetch;
  deviceId?: string;
  now?: () => number;
  onSignedOut?: () => void;
  /** Runs one refresh at a time across every tab sharing the store; defaults to the browser's Web Locks. */
  lock?: RefreshLock;
}

export type RefreshLock = <T>(fn: () => Promise<T>) => Promise<T>;

type LockManager = { request: (name: string, fn: () => Promise<unknown>) => Promise<unknown> };

/** Web Locks are per site, so every tab and the installed app take turns. Without them the server's
 * short grace for a token that was just replaced keeps a simultaneous refresh from signing out. */
const webLock: RefreshLock = (fn) => {
  const locks = (globalThis as { navigator?: { locks?: LockManager } }).navigator?.locks;
  return locks ? (locks.request('rydres.refresh', fn) as ReturnType<typeof fn>) : fn();
};

const newId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * For a form that creates something: the same answers keep the same Idempotency-Key, so sending
 * again after a dropped connection cannot make a second one; changed answers get a new key.
 */
export function formKey(): (body: unknown) => string {
  let sent = '';
  let id = newId();
  return (body) => {
    const now = JSON.stringify(body);
    if (now !== sent) {
      sent = now;
      id = newId();
    }
    return id;
  };
}

export interface CallOptions {
  body?: unknown;
  auth?: boolean;
  idempotent?: boolean;
  /** The Idempotency-Key to send, from `formKey`; otherwise `idempotent` sends a new one each call. */
  idempotencyKey?: string;
  query?: Record<string, string | number | undefined | null>;
}

/**
 * Typed client for the RydRes API. Attaches the access token, refreshes it before expiry or
 * on a 401, and sends an Idempotency-Key on calls that create bookings or move money.
 */
export class ApiClient {
  private session: Session | null = null;
  private refreshing: Promise<Session | null> | null = null;
  private readonly store: TokenStore;
  private readonly fetchFn: typeof fetch;
  private readonly now: () => number;
  private readonly lock: RefreshLock;
  readonly deviceId: string;

  constructor(private readonly opts: ApiClientOptions) {
    this.store = opts.store ?? new MemoryTokenStore();
    this.fetchFn = opts.fetchFn ?? ((input, init) => fetch(input, init));
    this.now = opts.now ?? Date.now;
    this.lock = opts.lock ?? webLock;
    this.deviceId = opts.deviceId ?? this.loadDeviceId();
  }

  private loadDeviceId(): string {
    try {
      const k = 'rydres.device';
      const v = globalThis.localStorage?.getItem(k);
      if (v) return v;
      const id = newId();
      globalThis.localStorage?.setItem(k, id);
      return id;
    } catch {
      return newId();
    }
  }

  async init(): Promise<Session | null> {
    this.session = (await this.store.load()) ?? null;
    return this.session;
  }

  get currentSession(): Session | null {
    return this.session;
  }

  // --- auth ------------------------------------------------------------------
  requestOtp(phone: string): Promise<{ ok: true; ref: string }> {
    return this.call('POST', '/auth/otp/request', { body: { phone }, auth: false });
  }

  /**
   * `intent: 'passenger'` signs in to a passenger account (rydres.com) instead of the driver app.
   * A phone app passes the id it keeps for itself, since it has no localStorage.
   */
  async verifyOtp(phone: string, code: string, intent?: 'driver' | 'passenger', deviceId = this.deviceId): Promise<Session> {
    const session = await this.call<Session>('POST', '/auth/otp/verify', {
      body: { phone, code, deviceId, ...(intent ? { intent } : {}) },
      auth: false,
    });
    await this.setSession(session);
    return session;
  }

  async refresh(): Promise<Session | null> {
    if (this.refreshing) return this.refreshing;
    const mine = this.session;
    if (!mine) return null;
    this.refreshing = this.lock(async () => {
      // Another tab sharing the stored session may have refreshed while this one waited: its
      // newer token is the one to use, and if its access token is still fresh nothing is sent.
      const stored = (await this.store.load()) ?? null;
      const current = stored && stored.user.id === mine.user.id && stored.refreshToken !== mine.refreshToken ? stored : mine;
      if (current !== mine && current.accessExpiresAt - this.now() > 30_000) {
        this.session = current;
        return current;
      }
      try {
        const next = await this.call<Omit<Session, 'user'>>('POST', '/auth/refresh', {
          body: { refreshToken: current.refreshToken },
          auth: false,
        });
        const session = { ...next, user: current.user };
        await this.setSession(session);
        return session;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await this.setSession(null);
          this.opts.onSignedOut?.();
          return null;
        }
        throw err;
      }
    }).finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  async signOut(): Promise<void> {
    const s = this.session;
    await this.setSession(null);
    if (s)
      await this.call('POST', '/auth/logout', {
        body: { refreshToken: s.refreshToken },
        auth: false,
      }).catch(() => undefined);
  }

  me(): Promise<Me> {
    return this.call('GET', '/auth/me');
  }

  // --- plumbing ---------------------------------------------------------------
  async call<T = unknown>(method: string, path: string, opts: CallOptions = {}): Promise<T> {
    const auth = opts.auth ?? true;
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    if (opts.idempotencyKey) headers[IDEMPOTENCY_HEADER] = opts.idempotencyKey;
    else if (opts.idempotent) headers[IDEMPOTENCY_HEADER] = newId();
    if (auth) {
      if (this.session && this.session.accessExpiresAt - this.now() < 30_000) await this.refresh();
      if (this.session) headers.authorization = `Bearer ${this.session.accessToken}`;
    }
    let url = `${this.opts.baseUrl}${path}`;
    if (opts.query) {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.query)) if (v !== undefined && v !== null) q.set(k, String(v));
      const s = q.toString();
      if (s) url += `${url.includes('?') ? '&' : '?'}${s}`;
    }
    const send = () =>
      this.fetchFn(url, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      });
    let res: Response;
    try {
      res = await send();
    } catch (err) {
      // One quick retry covers the few seconds an API container takes to come back after a deploy.
      await new Promise((r) => setTimeout(r, 1200));
      try {
        res = await send();
      } catch {
        throw new ApiError(0, { error: 'network', message: 'Could not reach the server. Check your connection.' });
      }
    }
    if ((res.status === 502 || res.status === 503) && method === 'GET') {
      await new Promise((r) => setTimeout(r, 1500));
      res = await send();
    }
    if (res.status === 401 && auth && this.session) {
      const refreshed = await this.refresh();
      if (refreshed) {
        headers.authorization = `Bearer ${refreshed.accessToken}`;
        res = await send();
      }
    }
    const text = await res.text();
    const body = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const err = (body as ApiErrorBody | null) ?? { error: 'http', message: res.statusText };
      throw new ApiError(res.status, err);
    }
    return body as T;
  }

  private async setSession(s: Session | null) {
    this.session = s;
    await this.store.save(s);
  }
}
